import {
  DAY,
  HOUR,
  type ReportColumnDefinition,
  type ReportColumnInput,
  type ReportColumnSuccess,
  type ReportLeaderboardColumn,
  type WorkflowId,
} from '@terros/common'
import { useMemo } from 'react'
import { type GetHookResponse } from '../hooks'
import { reportColumnKey, reportLeaderboardColumnsKey } from '../keys'
import { useTantalimQuery } from '../query'

export type ReportColumnVariantGroup = {
  representative: string
  allValues: string[]
  options: Map<string, string>
}

export type ReportColumnsMetadata = GetHookResponse<ReportColumnDefinition[]> & {
  columns: ReportColumnDefinition[]
  columnsMap: Map<string, ReportColumnDefinition>
  variantMap: Map<string, ReportColumnVariantGroup>
}

export function useReportColumns(
  input?: ReportColumnInput | WorkflowId,
  enabled = true
): GetHookResponse<ReportColumnDefinition[]> {
  const queryInput: ReportColumnInput = typeof input === 'string' ? { workflowId: input } : input ?? {}
  return useTantalimQuery<ReportColumnInput, ReportColumnSuccess, ReportColumnDefinition[]>({
    url: '/report/column',
    queryKey: reportColumnKey(queryInput),
    input: queryInput,
    convertResponse: (data) => data.columns,
    options: {
      enabled,
      staleTime: 6 * HOUR,
      gcTime: DAY,
    },
  })
}

export function useReportColumnsMap(workflowId?: WorkflowId): Map<string, ReportColumnDefinition> {
  const { data: reportColumns } = useReportColumns({ workflowId })

  return useMemo(() => toReportColumnsMap(reportColumns ?? []), [reportColumns])
}

export function useReportColumnsMetadata(
  input?: ReportColumnInput | WorkflowId,
  enabled = true
): ReportColumnsMetadata {
  const state = useReportColumns(input, enabled)
  const columns = state.data ?? []
  const columnsMap = useMemo(() => toReportColumnsMap(columns), [columns])
  const variantMap = useMemo(() => toReportColumnVariantMap(columns), [columns])

  return {
    ...state,
    columns,
    columnsMap,
    variantMap,
  }
}

export function toReportColumnsMap(columns: ReportColumnDefinition[]): Map<string, ReportColumnDefinition> {
  return new Map(columns.map((column) => [column.value, column]))
}

export function toReportColumnVariantMap(
  columns: ReportColumnDefinition[]
): Map<string, ReportColumnVariantGroup> {
  const groups = new Map<string, ReportColumnVariantGroup>()

  for (const column of columns) {
    if (!column.variant) continue
    const existing = groups.get(column.variant.id)
    if (existing) {
      existing.options.set(column.variant.option, column.value)
      existing.allValues.push(column.value)
      continue
    }

    groups.set(column.variant.id, {
      representative: column.value,
      allValues: [column.value],
      options: new Map([[column.variant.option, column.value]]),
    })
  }

  return groups
}

export function useLeaderboardColumns(workflowId?: WorkflowId): GetHookResponse<ReportLeaderboardColumn[]> {
  return useTantalimQuery<ReportColumnInput, ReportColumnSuccess, ReportLeaderboardColumn[]>({
    url: '/report/column',
    queryKey: reportLeaderboardColumnsKey(workflowId),
    input: { workflowId },
    convertResponse: (data) => data.leaderboardColumns ?? [],
    options: {
      staleTime: 6 * HOUR,
      gcTime: DAY,
    },
  })
}

export function useDefaultReportColumns(workflowId?: WorkflowId): GetHookResponse<string[]> {
  const state = useReportColumns({ workflowId })
  return {
    ...state,
    data: state.data?.filter((column) => column.default).map((c) => c.value),
  }
}
